/**
 * 🎬 Arena Cinematics — Feb 2026
 *
 * Three cinematic beats that wrap the existing FightOverlay without rewriting it:
 *   1. <IntroCutscene/>   — plays once per fight on mount (name plates → VS → ROUND 1 FIGHT!)
 *   2. <UltimateCutscene/> — fullscreen red radial bloom + slow-mo + ult-name banner on boss ultimates
 *   3. <PostFightCinema/>  — winner glow / loser slump / gold-rain shower / reward chip flight on victory
 *
 * SFX wiring:
 *   • IntroCutscene fires intro_gong → fight_call
 *   • UltimateCutscene fires ult_announce (already routed via sfxForLogEntry, but we double-fire the
 *     announcer-style version for the banner moment)
 *   • PostFightCinema fires victory_grand or defeat_grand + reward_chime as chips fly in
 *
 * All three components self-dismiss via setTimeout; parent only needs to mount them.
 */
import { useEffect, useRef, useState } from "react";
import { playSfx } from "@/utils/battleSfx";

/* ─── 1. Pre-fight intro: name plates → VS → ROUND 1 FIGHT! ─── */
export function IntroCutscene({ petName, cpuName, petTier, cpuTier, cpuIsBoss, onDone }) {
  const [phase, setPhase] = useState("plates"); // plates → fight → done
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    playSfx("intro_gong");
    const t1 = setTimeout(() => {
      setPhase("fight");
      playSfx("fight_call");
    }, 1100);
    const t2 = setTimeout(() => {
      setPhase("done");
      onDone?.();
    }, 2200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);

  if (phase === "done") return null;

  return (
    <div
      className="absolute inset-0 z-40 pointer-events-none flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm arena-fadeout"
      data-testid="arena-intro-cutscene"
    >
      {/* Top name plate — pet (slides in from left) */}
      <div className="arena-nameplate-pet absolute left-0 top-6 sm:top-10 pr-6 pl-3 py-2 sm:py-3 bg-gradient-to-r from-amber-600 via-amber-500 to-transparent border-r-4 border-amber-300 shadow-[0_0_20px_rgba(245,158,11,0.6)]">
        <div className="text-[10px] sm:text-xs uppercase tracking-widest text-amber-100/80 font-bold">CHALLENGER</div>
        <div className="text-xl sm:text-3xl font-black text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" style={{ fontFamily: "Anton, sans-serif" }}>
          {petName}
        </div>
        {petTier && (
          <div className="text-[10px] sm:text-xs text-amber-100/90 font-bold">Tier {petTier}</div>
        )}
      </div>

      {/* Bottom name plate — opponent (slides in from right) */}
      <div className="arena-nameplate-cpu absolute right-0 bottom-6 sm:bottom-10 pl-6 pr-3 py-2 sm:py-3 bg-gradient-to-l from-red-700 via-red-600 to-transparent border-l-4 border-red-300 shadow-[0_0_20px_rgba(220,38,38,0.6)] text-right">
        <div className="text-[10px] sm:text-xs uppercase tracking-widest text-red-100/80 font-bold">
          {cpuIsBoss ? "★ BOSS" : "OPPONENT"}
        </div>
        <div className="text-xl sm:text-3xl font-black text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" style={{ fontFamily: "Anton, sans-serif" }}>
          {cpuName}
        </div>
        {cpuTier != null && (
          <div className="text-[10px] sm:text-xs text-red-100/90 font-bold">Tier {cpuTier}</div>
        )}
      </div>

      {/* Center: VS badge then ROUND 1 FIGHT! */}
      {phase === "plates" && (
        <div className="arena-vs-pop text-7xl sm:text-9xl font-black text-amber-400 drop-shadow-[0_4px_12px_rgba(0,0,0,0.9)]" style={{ fontFamily: "Anton, sans-serif" }}>
          VS
        </div>
      )}
      {phase === "fight" && (
        <div className="flex flex-col items-center">
          <div className="text-base sm:text-xl tracking-[0.5em] text-amber-200 font-black uppercase opacity-90">ROUND 1</div>
          <div className="arena-fight-smash text-6xl sm:text-8xl font-black text-white drop-shadow-[0_0_24px_rgba(220,38,38,0.95)]" style={{ fontFamily: "Anton, sans-serif", textShadow: "0 0 30px #DC2626, 0 0 60px #DC2626" }}>
            FIGHT!
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── 2. Ultimate cinema: fullscreen red bloom + banner ─── */
export function UltimateCutscene({ active, ultName, casterName }) {
  const firedRef = useRef(false);
  useEffect(() => {
    if (!active) { firedRef.current = false; return; }
    if (firedRef.current) return;
    firedRef.current = true;
    playSfx("ult_announce");
  }, [active]);

  if (!active) return null;

  return (
    <>
      {/* Radial bloom — sits over the stage */}
      <div className="absolute inset-0 z-30 pointer-events-none arena-ult-radial" data-testid="arena-ult-radial" />

      {/* Ult banner at top of stage */}
      <div className="absolute top-0 left-0 right-0 z-40 pointer-events-none arena-ult-banner-in arena-ult-banner-out" data-testid="arena-ult-banner">
        <div className="mx-auto max-w-xl bg-gradient-to-r from-red-900 via-red-700 to-red-900 border-y-4 border-red-300 shadow-[0_0_40px_rgba(220,38,38,0.9)] py-2 sm:py-3 px-4 sm:px-6 text-center">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.6em] text-red-200 font-black animate-pulse">
            ⚠ ULTIMATE ⚠
          </div>
          <div className="text-2xl sm:text-4xl font-black text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.95)] leading-tight" style={{ fontFamily: "Anton, sans-serif" }}>
            {ultName || "ULTIMATE STRIKE"}
          </div>
          {casterName && (
            <div className="text-[10px] sm:text-sm text-red-100/90 font-bold uppercase tracking-wider mt-0.5">
              {casterName}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ─── 3. Post-fight cinema: winner pulse, loser slump, gold-rain, reward chip flight ─── */
export function PostFightCinema({ active, won, onSettled }) {
  const firedRef = useRef(false);
  useEffect(() => {
    if (!active) { firedRef.current = false; return; }
    if (firedRef.current) return;
    firedRef.current = true;
    if (won) {
      playSfx("victory_grand");
      // reward chip chime ~700ms later
      setTimeout(() => playSfx("reward_chime"), 700);
    } else {
      playSfx("defeat_grand");
    }
    setTimeout(() => onSettled?.(), 1800);
  }, [active, won, onSettled]);

  if (!active || !won) return null;

  // 12 gold shards raining down
  const shards = Array.from({ length: 12 }).map((_, i) => ({
    left: `${(i * 8.3 + (i % 2 === 0 ? 4 : 0)).toFixed(1)}%`,
    delay: `${(i * 60) % 800}ms`,
    icon: i % 3 === 0 ? "✨" : i % 3 === 1 ? "🪙" : "💰",
  }));

  return (
    <div
      className="absolute inset-0 z-30 pointer-events-none overflow-hidden"
      data-testid="arena-postfight-cinema"
    >
      {shards.map((s, i) => (
        <div
          key={i}
          className="arena-gold-rain-shard"
          style={{ left: s.left, top: "-10%", animationDelay: s.delay }}
        >
          {s.icon}
        </div>
      ))}
    </div>
  );
}
